// CMS page loader. Fetches a single dynamic page from its own configurable
// base, independent of loadAll() (which loads the global festival data set).
//
//   published:  {PAGES_BASE}pages/{slug}.json
//   preview:    {PAGES_BASE}preview/{guid}.json
//
// PAGES_BASE is a separate setting from the festival data base (VITE_DATA_BASE)
// even though both point at the same CDN in staging/production today: the
// BackOffice publishes pages/{slug}.json and preview/{guid}.json there via the
// editor's Publish action (same CdnPublishing config as program.json). Local
// dev leaves VITE_PAGES_BASE unset and falls back to the bundled /data/.
//
// Section bodies arrive as HTML and are sanitized here so consumers can render
// them directly via dangerouslySetInnerHTML.

import { sanitizeHtml } from './data.ts';
import type { PageData, PageSection, PhaseId } from './types.ts';

// Configurable via Vite env; falls back to bundled /data/. Normalized to a
// trailing slash so it can be provided with or without one.
const PAGES_BASE: string = (import.meta.env.VITE_PAGES_BASE || '/data/').replace(/\/?$/, '/');

async function fetchPage(path: string): Promise<PageData> {
  const res = await fetch(PAGES_BASE + path, { credentials: 'omit' });
  if (!res.ok) throw new Error(`Failed to load ${path} (HTTP ${res.status})`);
  const raw = (await res.json()) as PageData;
  return normalizePage(raw);
}

/** Pre-sanitize content sections' HTML bodies once at load time. */
function normalizePage(page: PageData): PageData {
  const sections: PageSection[] = page.sections.map(s =>
    s.type === 'content' ? { ...s, bodyHtml: sanitizeHtml(s.bodyHtml) } : s,
  );
  return { ...page, sections };
}

/** Load a published page by its (hardcoded) slug. */
export function loadPage(slug: string): Promise<PageData> {
  return fetchPage(`pages/${slug}.json`);
}

/** Load an unpublished preview snapshot by guid. */
export function loadPreview(guid: string): Promise<PageData> {
  return fetchPage(`preview/${guid}.json`);
}

/** Keeps a section if it has no phase restriction, or if it includes the
 *  active phase. When there is no active phase, only unrestricted sections
 *  are kept (CMS-014). */
export function filterSectionsByPhase(sections: readonly PageSection[], activePhase: PhaseId | null): readonly PageSection[] {
  return sections.filter(section => {
    const visiblePhases = section.visiblePhases;
    if (!visiblePhases || visiblePhases.length === 0) return true;
    if (activePhase === null) return false;
    return visiblePhases.includes(activePhase);
  });
}

/**
 * Resolving a page's hero for the active phase lives in `shared/phase.mjs`, so the server picks the
 * same hero — and therefore the same title and share card — that the visitor will see. Re-exported
 * here because the app has always reached for it through pages.ts.
 */
export { isHeroHidden, resolveHero } from '../../shared/phase.mjs';

/**
 * The address of a news story. A slug when the article has one — /nyheder/festivalmerch-er-klar
 * says something to a reader, to Google and in a share card, where a guid says nothing — and the
 * id while it does not, which is every article published before slugs existed and until the next
 * publish backfills them.
 */
export function newsStoryPath(item: { readonly id: string; readonly slug?: string }): string {
  return `/nyheder/${item.slug && item.slug !== '' ? item.slug : item.id}`;
}
